import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Paper,
  Stack,
  Tooltip,
  Trans,
  Typography,
  useTranslation,
} from "@ncino/pax-ui";
import { ArrowDownIcon, CheckIcon } from "@phosphor-icons/react";
import { SystemConfigs } from "@ncino/pa-authentication-client-webclient";
import { useEffect, useState } from "react";
import { useScrollGate } from "./useScrollGate";
import type { MarketplaceAgreementGateQuery } from "@/services/graphql/reactQuery";
import ncinoLogo from "@/assets/images/ncino-logo-mono-blue-RGB.png";
import { sanitizeHtml } from "@/shared/utility/sanitizeHtml";

export type Terms = NonNullable<
  NonNullable<MarketplaceAgreementGateQuery["agreement_getMarketplaceGate"]>["termsToAccept"]
>;

interface TermsStepProps {
  terms: Terms;
  onAccept: (version: number) => void | Promise<void>;
  isAccepting?: boolean;
  error?: boolean;
}

export function TermsStep({ terms, onAccept, isAccepting, error }: TermsStepProps) {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const { ref, elRef, hasRead, onScroll } = useScrollGate();

  useEffect(() => {
    const id = requestAnimationFrame(() => elRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [elRef]);

  return (
    <>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          "&&": { pt: 6 },
          pb: 5,
          px: 7,
        }}
      >
        <Stack spacing={2} alignItems="start" marginBottom={3}>
          <Box
            component="img"
            src={ncinoLogo}
            alt={t("agreementGate.logoAlt")}
            sx={{ height: 28, width: "auto", display: "block" }}
          />
          <Typography id="agreement-gate-title" variant="h4" fontWeight="fontWeightSemiBold">
            {t("agreementGate.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("agreementGate.subtitle")}
          </Typography>
        </Stack>
        <Typography
          id="agreement-gate-terms-heading"
          variant="subtitle2"
          fontWeight="fontWeightSemiBold"
          gutterBottom
        >
          {t("agreementGate.agreementHeader")}
        </Typography>

        <Paper
          ref={ref}
          onScroll={onScroll}
          variant="outlined"
          tabIndex={0}
          role="region"
          aria-labelledby="agreement-gate-terms-heading"
          data-testid="agreement-gate-scroll"
          sx={{ minHeight: 180, maxHeight: 320, overflowY: "auto", p: 2 }}
        >
          {/*
            Agreement content is HTML authored by nCino admins in the Froala editor and
            stored on the version record. The authoring surface is internal-only and gated
            by elevated rights.
          */}
          <Box
            sx={{ typography: "body2" }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(terms.content) }}
          />
        </Paper>
        <Stack
          id="agreement-gate-scroll-status"
          direction="row"
          spacing={0.5}
          alignItems="center"
          aria-live="polite"
          sx={{
            mt: 1.5,
            color: hasRead ? "success.main" : "text.disabled",
            fontSize: (theme) => theme.typography.caption.fontSize,
          }}
        >
          {hasRead ? <CheckIcon weight="bold" /> : <ArrowDownIcon weight="bold" />}
          <Typography variant="caption" sx={{ fontWeight: "fontWeightSemiBold" }}>
            {t(hasRead ? "agreementGate.scrollComplete" : "agreementGate.scrollPrompt")}
          </Typography>
        </Stack>
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              disabled={!hasRead}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="agreement-gate-checkbox"
              aria-describedby="agreement-gate-scroll-status"
            />
          }
          label={
            <Typography variant="body2">
              <Trans
                i18nKey="agreementGate.checkboxLabel"
                components={{ bold: <Box component="span" fontWeight="fontWeightBold" /> }}
              />
            </Typography>
          }
          sx={{ marginTop: 2.5 }}
        />
      </DialogContent>

      {error && (
        <Alert severity="error" sx={{ mx: 7, mb: 2 }}>
          {t("agreementGate.acceptError")}
        </Alert>
      )}

      <DialogActions
        sx={{
          px: 7,
          pb: 4,
          pt: 4,
          backgroundColor: "grey.100",
        }}
      >
        <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
          <Tooltip title={agreed ? "" : t("agreementGate.continueTooltip")} placement="top">
            <Box component="span" sx={{ width: "100%", display: "block" }}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                disabled={!agreed}
                loading={isAccepting}
                onClick={() => onAccept(terms.version)}
                data-testid="agreement-gate-continue"
              >
                {t("agreementGate.continue")}
              </Button>
            </Box>
          </Tooltip>
          <Button
            fullWidth
            variant="text"
            onClick={() => {
              window.location.href = SystemConfigs.paBaseUrl();
            }}
            data-testid={"agreement-gate-not-now"}
          >
            {t("agreementGate.notNow")}
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
}
